import { Box, Container, Typography, Button, Grid, Card, CardContent, useTheme } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import SpeedIcon from '@mui/icons-material/Speed';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { FEATURES, HOW_IT_WORKS } from '../../utils/constants';

// Animation variants
const fadeInUp = {
    initial: { opacity: 0, y: 40 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 },
};

const staggerContainer = {
    animate: {
        transition: {
            staggerChildren: 0.1,
        },
    },
};

// Icon mapping
const iconMap: Record<string, React.ElementType> = {
    CloudUpload: CloudUploadIcon,
    Speed: SpeedIcon,
    PictureAsPdf: PictureAsPdfIcon,
    Description: DescriptionIcon,
};

const LandingPage = () => {
    const navigate = useNavigate();
    const theme = useTheme();

    return (
        <Box>
            {/* Hero Section */}
            <Box
                sx={{
                    minHeight: '80vh',
                    display: 'flex',
                    alignItems: 'center',
                    background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%)',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: -100,
                        right: -100,
                        width: 400,
                        height: 400,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, rgba(25,118,210,0.1) 0%, rgba(66,165,245,0.1) 100%)',
                    },
                    '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: -150,
                        left: -100,
                        width: 500,
                        height: 500,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, rgba(156,39,176,0.05) 0%, rgba(186,104,200,0.05) 100%)',
                    },
                }}
            >
                <Container maxWidth="lg">
                    <Grid container spacing={6} alignItems="center">
                        <Grid item xs={12} md={6}>
                            <motion.div
                                initial={{ opacity: 0, x: -50 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.8 }}
                            >
                                <Typography
                                    variant="overline"
                                    sx={{
                                        color: 'primary.main',
                                        fontWeight: 600,
                                        letterSpacing: 2,
                                        mb: 2,
                                        display: 'block',
                                    }}
                                >
                                    GIS ANALYSIS
                                </Typography>
                                <Typography
                                    variant="h1"
                                    sx={{
                                        fontWeight: 700,
                                        fontSize: { xs: '2.5rem', md: '3.5rem' },
                                        lineHeight: 1.2,
                                        mb: 3,
                                        background: 'linear-gradient(135deg, #1976d2 0%, #9c27b0 100%)',
                                        backgroundClip: 'text',
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                    }}
                                >
                                    Automated GIS Spatial Analysis
                                </Typography>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        color: 'text.secondary',
                                        fontWeight: 400,
                                        lineHeight: 1.6,
                                        mb: 4,
                                        maxWidth: 500,
                                    }}
                                >
                                    Upload your spatial files, get complete analysis and download professional PDF reports.
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                    <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                                        <Button
                                            variant="contained"
                                            size="large"
                                            endIcon={<ArrowForwardIcon />}
                                            onClick={() => navigate('/upload')}
                                            sx={{
                                                px: 4,
                                                py: 1.5,
                                                fontSize: '1rem',
                                                background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                                                boxShadow: '0 8px 24px rgba(25, 118, 210, 0.35)',
                                                '&:hover': {
                                                    background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)',
                                                    boxShadow: '0 12px 32px rgba(25, 118, 210, 0.45)',
                                                },
                                            }}
                                        >
                                            Start Analysis
                                        </Button>
                                    </motion.div>
                                    <Button
                                        variant="outlined"
                                        size="large"
                                        onClick={() => {
                                            document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' });
                                        }}
                                        sx={{
                                            px: 4,
                                            py: 1.5,
                                            borderWidth: 2,
                                            '&:hover': { borderWidth: 2 },
                                        }}
                                    >
                                        Learn More
                                    </Button>
                                </Box>
                            </motion.div>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <motion.div
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.8, delay: 0.2 }}
                            >
                                <Box
                                    sx={{
                                        position: 'relative',
                                        '&::before': {
                                            content: '""',
                                            position: 'absolute',
                                            inset: -20,
                                            background: 'linear-gradient(135deg, rgba(25,118,210,0.2) 0%, rgba(156,39,176,0.1) 100%)',
                                            borderRadius: 4,
                                            filter: 'blur(40px)',
                                            zIndex: 0,
                                        },
                                    }}
                                >
                                    <Box
                                        component="svg"
                                        viewBox="0 0 400 300"
                                        sx={{
                                            width: '100%',
                                            height: 'auto',
                                            position: 'relative',
                                            zIndex: 1,
                                            filter: 'drop-shadow(0 20px 40px rgba(0,0,0,0.1))',
                                        }}
                                    >
                                        <defs>
                                            <linearGradient id="bgGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                                <stop offset="0%" stopColor="#e8f5e9" />
                                                <stop offset="100%" stopColor="#c8e6c9" />
                                            </linearGradient>
                                            <linearGradient id="parcelGradient1" x1="0%" y1="0%" x2="100%" y2="100%">
                                                <stop offset="0%" stopColor="#1976d2" />
                                                <stop offset="100%" stopColor="#42a5f5" />
                                            </linearGradient>
                                            <linearGradient id="parcelGradient2" x1="0%" y1="0%" x2="100%" y2="100%">
                                                <stop offset="0%" stopColor="#9c27b0" />
                                                <stop offset="100%" stopColor="#ba68c8" />
                                            </linearGradient>
                                        </defs>
                                        <rect fill="url(#bgGradient)" width="400" height="300" rx="16" />
                                        <motion.path
                                            d="M50,100 L150,70 L200,110 L180,170 L80,150 Z"
                                            fill="url(#parcelGradient1)"
                                            stroke="#1565c0"
                                            strokeWidth="2"
                                            initial={{ pathLength: 0 }}
                                            animate={{ pathLength: 1 }}
                                            transition={{ duration: 1.5, delay: 0.5 }}
                                        />
                                        <motion.path
                                            d="M200,110 L330,90 L360,150 L290,190 L180,170 Z"
                                            fill="url(#parcelGradient2)"
                                            stroke="#7b1fa2"
                                            strokeWidth="2"
                                            initial={{ pathLength: 0 }}
                                            animate={{ pathLength: 1 }}
                                            transition={{ duration: 1.5, delay: 0.8 }}
                                        />
                                        <motion.path
                                            d="M80,150 L180,170 L160,240 L60,220 Z"
                                            fill="#4caf50"
                                            stroke="#2e7d32"
                                            strokeWidth="2"
                                            initial={{ pathLength: 0 }}
                                            animate={{ pathLength: 1 }}
                                            transition={{ duration: 1.5, delay: 1.1 }}
                                        />
                                        <motion.circle
                                            cx="180"
                                            cy="145"
                                            r="6"
                                            fill="#1976d2"
                                            initial={{ scale: 0 }}
                                            animate={{ scale: 1 }}
                                            transition={{ duration: 0.5, delay: 1.5 }}
                                        />
                                        <text x="190" y="142" fontSize="10" fill="#1976d2" fontWeight="bold">
                                            Center
                                        </text>
                                    </Box>
                                </Box>
                            </motion.div>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* Features Section */}
            <Box
                id="features"
                sx={{
                    py: { xs: 8, md: 12 },
                    backgroundColor: 'white',
                }}
            >
                <Container maxWidth="lg">
                    <motion.div
                        initial="initial"
                        whileInView="animate"
                        viewport={{ once: true, margin: '-100px' }}
                        variants={staggerContainer}
                    >
                        <Box sx={{ textAlign: 'center', mb: 8 }}>
                            <motion.div variants={fadeInUp}>
                                <Typography
                                    variant="overline"
                                    sx={{ color: 'primary.main', fontWeight: 600, mb: 1, display: 'block' }}
                                >
                                    TOP FEATURES
                                </Typography>
                                <Typography
                                    variant="h2"
                                    sx={{ fontWeight: 700, mb: 2 }}
                                >
                                    Why Choose Us?
                                </Typography>
                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                    sx={{ maxWidth: 600, mx: 'auto' }}
                                >
                                    Complete solution for GIS spatial analysis with the latest technology
                                </Typography>
                            </motion.div>
                        </Box>

                        <Grid container spacing={4}>
                            {FEATURES.map((feature, index) => {
                                const IconComponent = iconMap[feature.icon] || DescriptionIcon;
                                return (
                                    <Grid item xs={12} sm={6} md={3} key={index}>
                                        <motion.div
                                            variants={fadeInUp}
                                            whileHover={{ y: -8 }}
                                            transition={{ type: 'spring', stiffness: 300 }}
                                        >
                                            <Card
                                                sx={{
                                                    height: '100%',
                                                    textAlign: 'center',
                                                    p: 3,
                                                    border: '1px solid',
                                                    borderColor: 'divider',
                                                    transition: 'all 0.3s ease',
                                                    '&:hover': {
                                                        borderColor: 'primary.main',
                                                        boxShadow: '0 12px 40px rgba(25, 118, 210, 0.15)',
                                                    },
                                                }}
                                            >
                                                <CardContent>
                                                    <Box
                                                        sx={{
                                                            width: 72,
                                                            height: 72,
                                                            borderRadius: 3,
                                                            background: `linear-gradient(135deg, ${theme.palette.primary.light}20 0%, ${theme.palette.primary.main}20 100%)`,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            mx: 'auto',
                                                            mb: 3,
                                                        }}
                                                    >
                                                        <IconComponent
                                                            sx={{
                                                                fontSize: 36,
                                                                color: 'primary.main',
                                                            }}
                                                        />
                                                    </Box>
                                                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5 }}>
                                                        {feature.title}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {feature.description}
                                                    </Typography>
                                                </CardContent>
                                            </Card>
                                        </motion.div>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    </motion.div>
                </Container>
            </Box>

            {/* How It Works Section */}
            <Box
                id="how-it-works"
                sx={{
                    py: { xs: 8, md: 12 },
                    backgroundColor: 'grey.50',
                }}
            >
                <Container maxWidth="lg">
                    <motion.div
                        initial="initial"
                        whileInView="animate"
                        viewport={{ once: true, margin: '-100px' }}
                        variants={staggerContainer}
                    >
                        <Box sx={{ textAlign: 'center', mb: 8 }}>
                            <motion.div variants={fadeInUp}>
                                <Typography
                                    variant="overline"
                                    sx={{ color: 'secondary.main', fontWeight: 600, mb: 1, display: 'block' }}
                                >
                                    HOW IT WORKS
                                </Typography>
                                <Typography variant="h2" sx={{ fontWeight: 700, mb: 2 }}>
                                    Easy & Fast Process
                                </Typography>
                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                    sx={{ maxWidth: 600, mx: 'auto' }}
                                >
                                    Just 4 simple steps to get complete analysis results
                                </Typography>
                            </motion.div>
                        </Box>

                        <Grid container spacing={4} alignItems="center">
                            {HOW_IT_WORKS.map((step, index) => (
                                <Grid item xs={12} sm={6} md={3} key={index}>
                                    <motion.div
                                        variants={fadeInUp}
                                        whileHover={{ scale: 1.02 }}
                                    >
                                        <Box
                                            sx={{
                                                textAlign: 'center',
                                                position: 'relative',
                                                '&::after': index < HOW_IT_WORKS.length - 1 ? {
                                                    content: '""',
                                                    position: 'absolute',
                                                    top: 40,
                                                    right: { xs: '50%', md: -20 },
                                                    width: { xs: 2, md: 40 },
                                                    height: { xs: 40, md: 2 },
                                                    background: `linear-gradient(${index % 2 === 0 ? '90deg' : '90deg'}, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                                                    display: { xs: 'none', sm: 'block' },
                                                } : undefined,
                                            }}
                                        >
                                            <motion.div
                                                initial={{ scale: 0 }}
                                                whileInView={{ scale: 1 }}
                                                viewport={{ once: true }}
                                                transition={{ duration: 0.5, delay: index * 0.15 }}
                                            >
                                                <Box
                                                    sx={{
                                                        width: 80,
                                                        height: 80,
                                                        borderRadius: '50%',
                                                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        mx: 'auto',
                                                        mb: 3,
                                                        boxShadow: '0 8px 24px rgba(25, 118, 210, 0.3)',
                                                    }}
                                                >
                                                    <Typography
                                                        variant="h4"
                                                        sx={{ color: 'white', fontWeight: 700 }}
                                                    >
                                                        {step.step}
                                                    </Typography>
                                                </Box>
                                            </motion.div>
                                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                                                {step.title}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {step.description}
                                            </Typography>
                                        </Box>
                                    </motion.div>
                                </Grid>
                            ))}
                        </Grid>
                    </motion.div>
                </Container>
            </Box>

            {/* CTA Section */}
            <Box
                sx={{
                    py: { xs: 8, md: 12 },
                    background: 'linear-gradient(135deg, #1976d2 0%, #9c27b0 100%)',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.05\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
                    },
                }}
            >
                <Container maxWidth="md">
                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <Box sx={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
                            <Typography
                                variant="h3"
                                sx={{
                                    color: 'white',
                                    fontWeight: 700,
                                    mb: 3,
                                }}
                            >
                                Ready to Start?
                            </Typography>
                            <Typography
                                variant="h6"
                                sx={{
                                    color: 'rgba(255,255,255,0.9)',
                                    fontWeight: 400,
                                    mb: 4,
                                    maxWidth: 600,
                                    mx: 'auto',
                                }}
                            >
                                Upload your spatial files now and get analysis results in minutes
                            </Typography>
                            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                                <Button
                                    variant="contained"
                                    size="large"
                                    endIcon={<ArrowForwardIcon />}
                                    onClick={() => navigate('/upload')}
                                    sx={{
                                        px: 5,
                                        py: 2,
                                        fontSize: '1.1rem',
                                        backgroundColor: 'white',
                                        color: 'primary.main',
                                        boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                                        '&:hover': {
                                            backgroundColor: 'rgba(255,255,255,0.95)',
                                            boxShadow: '0 12px 32px rgba(0,0,0,0.3)',
                                        },
                                    }}
                                >
                                    Start Now - It's Free!
                                </Button>
                            </motion.div>
                        </Box>
                    </motion.div>
                </Container>
            </Box>
        </Box>
    );
};

export default LandingPage;
